import { useEffect, useLayoutEffect } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { useWorkspaceStore } from '../../core/store/workspaceStore';
import { generateQueryName, deduplicateName, renumberDuplicateNames } from '../utils/queryNameGenerator';

type UseAutoTabTitleParams = {
  tabId: string;
  /** The (debounced) query text driving the auto-generated title. */
  query: string;
  /** User has manually renamed the tab — auto-titling must never override. */
  isCustomTitle: boolean;
  /** File-backed tabs keep the file name as title. */
  isFileTied: boolean;
};

/**
 * Auto-updates the tab title from the query text (debounced, only while the
 * user hasn't set a custom title and the tab isn't file-backed), including
 * dedupe + renumbering of same-base-name siblings. Behavior carried verbatim
 * from the previous inline implementation.
 */
export function useAutoTabTitle({
  tabId,
  query,
  isCustomTitle,
  isFileTied,
}: UseAutoTabTitleParams) {
  const updateTabTitleDebounced = useDebouncedCallback(
    (newQuery: string) => {
      const activeElement = document.activeElement;
      const activeTestId =
        activeElement instanceof HTMLElement ? activeElement.getAttribute('data-testid') : null;
      if (activeTestId?.startsWith('tab-edit-input-')) {
        return;
      }

      const { tabs, updateTabTitle, batchUpdateTabTitles } = useWorkspaceStore.getState();
      const latestTab = tabs.find((tab) => tab.id === tabId);

      if (!latestTab || latestTab.customTitle) {
        return;
      }

      // File-backed tabs keep the file name as title
      if (latestTab.type === 'query' && latestTab.content?.filePath) {
        return;
      }

      if (!newQuery.trim()) {
        return;
      }

      const autoName = generateQueryName(newQuery);
      if (autoName === 'Query' && /^Query-\d+$/.test(latestTab.title)) {
        return;
      }
      // Only update if the name would be different and meaningful
      if (autoName && autoName !== latestTab.title) {
        const existingTitles = tabs.filter((tab) => tab.id !== tabId).map((tab) => tab.title);
        const uniqueTitle = deduplicateName(autoName, existingTitles);
        updateTabTitle(tabId, uniqueTitle, false);

        // Renumber all tabs with the same base name if needed
        // We need to get fresh tabs state after the update
        setTimeout(() => {
          const freshTabs = useWorkspaceStore.getState().tabs;
          const renameMap = renumberDuplicateNames(autoName, freshTabs);
          if (renameMap) {
            batchUpdateTabTitles(renameMap);
          }
        }, 0);
      }
    },
    100 // 100ms debounce
  );

  // If the user switches to a custom title, cancel any pending auto-title update immediately
  // so it can't override the manual rename.
  useLayoutEffect(() => {
    if (isCustomTitle || isFileTied) {
      updateTabTitleDebounced.cancel();
    }
  }, [isCustomTitle, isFileTied, updateTabTitleDebounced]);

  useEffect(() => {
    // Skip if user has set a custom title
    if (isCustomTitle || isFileTied) {
      return;
    }
    updateTabTitleDebounced(query);
  }, [query, isCustomTitle, isFileTied, updateTabTitleDebounced]);
}
